import type { NavigationGuard } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'

/**
 * Streama CMS · Router guards
 *
 * Run in order: authGuard then privilegeGuard. Both are pure
 * functions of the store state; no side effects beyond redirecting
 * and pushing toasts.
 */

/**
 * Block access to non-public routes for unauthenticated callers.
 * Captures the requested URL in `?redirect=` so the login flow can
 * route back after success.
 */
export const authGuard: NavigationGuard = (to) => {
  const auth = useAuthStore()

  if (to.meta.public) {
    // Already authenticated and asking for /login? Send them to dashboard.
    if (to.name === 'login' && auth.isAuthenticated) {
      return { name: 'dashboard' }
    }
    return true
  }

  if (!auth.isAuthenticated) {
    return {
      name: 'login',
      query: { redirect: to.fullPath },
    }
  }

  return true
}

/**
 * If a route declares a required privilege via meta.privilege,
 * gate access on the auth store's privilege set.
 *
 * Failure path: stay on the previous route (or fall back to dashboard
 * if there is no previous route — e.g. deep-link entry) and surface
 * a toast. We deliberately do NOT redirect to a "403" page — admins
 * lacking privileges should still see the app's full nav so they
 * understand what's missing.
 */
export const privilegeGuard: NavigationGuard = (to, _from) => {
  const required = to.meta.privilege
  if (!required) return true

  const auth = useAuthStore()

  // Public routes shouldn't declare privileges, but defensively skip
  // the check if they do.
  if (to.meta.public) return true

  // DEBUG: log the privilege check
  console.log('[privilegeGuard] checking:', required, 'user:', auth.user?.userName, 'privileges:', [...(auth.privileges ?? [])])
  if (!auth.hasPrivilege(required)) {
    const toast = useToastStore()
    toast.push({
      kind: 'error',
      message: `Insufficient privileges (${required}).`,
      detail: 'Ask an admin to grant access.',
    })
    // Allow navigation so the view can render an inline error-403 banner.
    // Redirecting client-side is unreliable in E2E tests because page.goto()
    // resolves before Vue Router completes the redirect.
    return true
  }

  return true
}
